/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  Skeleton,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useGetStatisticData from "@/hooks/useGetStatisticData";
import useGetObservationStatistics from "@/hooks/useGetObservationStatistics";
import { useTranslation } from "react-i18next";

const datasetNameFieldMap = {
  ptwPerWorkflow: "workflow_name",
  ptwMonthlyTrend: "month",
  obsPerSite: "site_name",
  obsCompletion: "duration_in_hours",
  obsMonthlyTrend: "month",
};

const createDynamicHistogramBins = (
  data,
  centerPointHours,
  rangeWidthHours,
  defaultCenter
) => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];

  const durationsInHours = data.map((item) => item.duration_in_seconds / 3600);
  const center =
    centerPointHours ||
    defaultCenter ||
    durationsInHours.reduce((sum, val) => sum + val, 0) /
      durationsInHours.length;
  const binWidth = rangeWidthHours || 50;
  const numBins = 10;

  const bins = [];
  const startPoint = Math.max(0, center - (numBins / 2) * binWidth);
  for (let i = 0; i <= numBins; i++) {
    const binMin = startPoint + i * binWidth;
    const binMax = startPoint + (i + 1) * binWidth;
    bins.push({
      min: binMin,
      max: binMax,
      count: 0,
      range:
        i === numBins
          ? `>${Math.round(binMin)}`
          : `${Math.round(binMin)}-${Math.round(binMax)}`,
    });
  }

  durationsInHours.forEach((duration) => {
    const bin = bins.find(
      (b, index) =>
        (index === numBins && duration >= b.min) ||
        (duration >= b.min && duration < b.max)
    );
    if (bin) bin.count += 1;
  });

  return bins.map((bin) => ({
    name: bin.range,
    count: bin.count,
  }));
};

export default function BarChartComponent({
  dataType = "ptw",
  selectedDataset,
  isModalView = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const ptwData = useGetStatisticData();
  const obsData = useGetObservationStatistics();

  const {
    counts: ptwCounts,
    trends: ptwTrends,
    isLoading: ptwLoading,
    error: ptwError,
  } = ptwData;
  const {
    counts: obsCounts,
    trends: obsTrends,
    isLoading: obsLoading,
    error: obsError,
  } = obsData;

  const isLoading = dataType === "ptw" ? ptwLoading : obsLoading;
  const error = dataType === "ptw" ? ptwError : obsError;
  const counts = dataType === "ptw" ? ptwCounts : obsCounts;

  const [centerPoint, setCenterPoint] = useState("");
  const [rangeWidth, setRangeWidth] = useState(
    selectedDataset === "obsCompletion" ? 100 : 50
  );

  useEffect(() => {
    if (selectedDataset === "obsCompletion") {
      setRangeWidth(100);
    } else {
      setRangeWidth(50);
    }
  }, [selectedDataset]);

  useEffect(() => {
    if (selectedDataset === "obsCompletion" && isModalView) {
      const defaultCenter = obsCounts?.obsCompletionSummary
        ?.average_duration_in_seconds
        ? (
            obsCounts.obsCompletionSummary.average_duration_in_seconds / 3600
          ).toFixed(2)
        : obsCounts?.obsCompletion && obsCounts.obsCompletion.length > 0
          ? (
              obsCounts.obsCompletion.reduce(
                (sum, item) => sum + item.duration_in_seconds,
                0
              ) /
              obsCounts.obsCompletion.length /
              3600
            ).toFixed(2)
          : "";
      setCenterPoint((prev) => (prev === "" ? defaultCenter : prev));
    } else {
      setCenterPoint("");
    }
  }, [selectedDataset, obsCounts, isModalView]);

  const chartData = useMemo(() => {
    if (selectedDataset === "ptwMonthlyTrend" && dataType === "ptw") {
      const rawData = ptwTrends?.ptwMonthlyTrend;
      if (!rawData || !Array.isArray(rawData)) return [];
      return rawData.map((item) => ({
        name: new Date(item.month).toLocaleString(i18n.language, {
          month: "short",
          year: "numeric",
        }),
        count: item.count || 0,
      }));
    }

    if (selectedDataset === "obsMonthlyTrend" && dataType === "observations") {
      const rawData = obsTrends?.obsMonthlyTrend;
      if (!rawData || !Array.isArray(rawData)) return [];
      return rawData.map((item) => ({
        name: new Date(item.month).toLocaleString(i18n.language, {
          month: "short",
          year: "numeric",
        }),
        count: item.count || 0,
      }));
    }

    if (selectedDataset === "obsCompletion" && dataType === "observations") {
      return createDynamicHistogramBins(
        obsCounts?.obsCompletion,
        centerPoint ? parseFloat(centerPoint) : null,
        rangeWidth,
        obsCounts?.obsCompletionSummary?.average_duration_in_seconds / 3600
      );
    }

    const rawData = counts?.[selectedDataset];
    const fieldName = datasetNameFieldMap[selectedDataset];
    if (!rawData || !Array.isArray(rawData)) return [];

    return rawData
      .map((item) => ({
        name: item[fieldName] || t("barChart.unknownLabel"),
        count: item.count || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [
    counts,
    ptwTrends,
    obsTrends,
    selectedDataset,
    centerPoint,
    rangeWidth,
    dataType,
    t,
    i18n.language,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: isModalView ? 3 : 0,
          bgcolor: theme.palette.dashboard.background.card,
        }}
      >
        {isModalView && selectedDataset === "obsCompletion" && (
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: theme.palette.dashboard.background.elevated,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton
                variant="rectangular"
                width={150}
                height={56}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                width={150}
                height={56}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Card>
        )}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={isModalView ? 400 : 200}
          sx={{ borderRadius: 1 }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: isModalView ? 3 : 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t("barChart.errorText")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("barChart.errorSupportText")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 3,
          flexDirection: "column",
          gap: 2,
          bgcolor: theme.palette.dashboard.background.card,
        }}
      >
        <Typography
          color={theme.palette.dashboard.text.secondary}
          variant="body1"
          textAlign="center"
        >
          {t("barChart.noDataText")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.dashboard.background.card,
      }}
    >
      {/* Controls for obsCompletion histogram */}
      {isModalView && selectedDataset === "obsCompletion" && (
        <Card
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: theme.palette.dashboard.background.subtle,
            boxShadow: theme.palette.dashboard.shadow.card,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.dashboard.text.primary,
              fontSize: 16,
            }}
          >
            {t("barChart.histogramConfigTitle")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: theme.palette.dashboard.text.secondary,
            }}
          >
            {t("barChart.histogramConfigSubtitle")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <TextField
              label={t("barChart.centerPointLabel")}
              value={centerPoint}
              onChange={(e) => setCenterPoint(e.target.value)}
              size="small"
              type="number"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("barChart.hoursUnit")}
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                minWidth: 150,
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.dashboard.background.elevated,
                  "& fieldset": {
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <TextField
              label={t("barChart.rangeWidthLabel")}
              value={rangeWidth}
              onChange={(e) => setRangeWidth(Number(e.target.value))}
              size="small"
              type="number"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("barChart.hoursUnit")}
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                minWidth: 150,
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.dashboard.background.elevated,
                  "& fieldset": {
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
          </Box>
        </Card>
      )}

      {/* Chart Container */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: isModalView ? 400 : 200,
          p: 1,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
              opacity={0.3}
            />
            <XAxis
              dataKey="name"
              tick={{
                fontSize: 12,
                fill: theme.palette.dashboard.text.secondary,
              }}
              stroke={theme.palette.dashboard.text.secondary}
            />
            <YAxis
              tick={{
                fontSize: 12,
                fill: theme.palette.dashboard.text.secondary,
              }}
              stroke={theme.palette.dashboard.text.secondary}
            />
            {isModalView && (
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.dashboard.background.elevated,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.palette.dashboard.shadow.card,
                  color: theme.palette.dashboard.text.primary,
                }}
                labelStyle={{
                  color: theme.palette.dashboard.text.primary,
                  fontWeight: 600,
                }}
              />
            )}
            <Bar
              dataKey="count"
              fill={theme.palette.dashboard.chart.primary}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
